import React, { useState } from 'react';
import PropTypes from 'prop-types';

class AutocompleteFieldResults extends React.Component {
  static propTypes = {
    results: PropTypes.array,
    didScroll: PropTypes.func,
    currentlySelected: PropTypes.object,
    onSelectResult: PropTypes.func,
    type: PropTypes.string
  };

  componentDidUpdate(prevProps) {
    if (prevProps.currentlySelected !== this.props.currentlySelected) {
      this.updateScrollPosition();
    }
  }

  render() {
    return (
      <div className="AutocompleteFieldResults" data-testid="results">
        <div
          className="AutocompleteFieldResults-content"
          ref={(ref) => (this.contentWrapper = ref)}
          onScroll={this.props.didScroll}
        >
          {this.props.results.map(this.renderResult)}
        </div>
      </div>
    );
  }

  renderResult = (result) => {
    return (
      <button
        type="button"
        key={result.id}
        ref={(el) => this.createRefForSelected(el, result)}
        className={this.resultClassName(result)}
        role="option"
        aria-selected={this.props.currentlySelected === result}
        tabIndex={0}
        onClick={() => this.props.onSelectResult(result)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.props.onSelectResult(result);
          }
        }}
      >
        {this.renderContent(result)}
      </button>
    );
  };

  createRefForSelected(el, result) {
    if (this.props.currentlySelected === result) {
      this.selectedRow = el;
    }
  }

  renderContent(result) {
    switch (this.props.type) {
      case 'authors':
        return <Author result={result} />;
      case 'mention_sources':
      case 'mention_sources_types':
        return <MentionSource result={result} />;
      case 'journals':
        return <Journal result={result} />;
      case 'grants':
        return <Grant result={result} />;
      case 'publishers':
      case 'doi_prefixes':
      case 'departments':
      case 'countries':
      case 'handle_prefixes':
      case 'affiliations':
      case 'field_of_research_codes':
      case 'funders':
      case 'sustainable_development_goals':
      case 'researchers':
        return <GenericResult result={result} />;
    }
  }

  resultClassName(result) {
    const selected = this.props.currentlySelected === result;

    return `AutocompleteFieldResults-row ${selected ? 'selected' : ''}`;
  }

  updateScrollPosition() {
    if (!this.selectedRow) return;

    const rowTop = this.selectedRow.offsetTop;
    const rowHeight = this.selectedRow.offsetHeight;
    const currentScroll = this.contentWrapper.scrollTop;
    const wrapperHeight = this.contentWrapper.offsetHeight;

    if (rowTop + rowHeight - currentScroll > wrapperHeight) {
      const scrollAmount = rowTop + rowHeight - wrapperHeight;
      this.contentWrapper.scrollTop = scrollAmount;
    } else if (rowTop - currentScroll < 0) {
      this.contentWrapper.scrollTop = rowTop;
    }
  }
}

const Author = ({ result }) => (
  <div className="AutocompleteFieldResults-row-content">
    <span className="title"> {result.description}</span>
    <span className="meta">{result.formattedDepartments}</span>
  </div>
);

const Journal = ({ result }) => (
  <div className="AutocompleteFieldResults-row-content">
    <span className="title">{result.title}</span>
    {result.issns.length > 0 && (
      <span className="meta">
        {I18n.t('Explorer.AdvancedSearch.fields.journal.issn_list', {
          issns: result.issns.join(', ')
        })}
      </span>
    )}
  </div>
);

const MentionSource = ({ result }) => {
  const [imageError, setImageError] = useState(false);

  if (result.type === 'mention_source') {
    return (
      <div className="AutocompleteFieldResults-row-content">
        <div className={`image ${imageError ? 'missing-image' : 'present-image'}`}>
          <img src={result.imageURL} onError={() => setImageError(true)} alt="" />
        </div>
        <div className="content">
          <span className="meta">{result.title}</span>
          <span className="name">{result.description}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="AutocompleteFieldResults-row-content">
      <div className={`image source-icon ${result.postType}`} />
      <div className="content">
        <span className="name">{result.title}</span>
        <span className="meta">{result.description}</span>
      </div>
    </div>
  );
};

const Grant = ({ result }) => {
  return (
    <div className="AutocompleteFieldResults-row-content">
      <span className="name">{result.name}</span>
      <span className="meta">{result.projectName}</span>
    </div>
  );
};

const GenericResult = ({ result }) => {
  return (
    <div className="AutocompleteFieldResults-row-content">
      <span className="title">{result.description}</span>
    </div>
  );
};

GenericResult.propTypes = {
  children: PropTypes.node,
  result: PropTypes.object
};

export default AutocompleteFieldResults;
